import React, { useMemo, useState, useEffect, useRef } from "react";
import { updateRelationRow } from "../services/data";

export default function RelationRowEditor({
  user,
  typeKey,
  row,
  fieldsMeta = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(() => sanitizeRow(row));
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => setForm(sanitizeRow(row)), [row]);

  // Lock scroll + ESC pour fermer (même UX que FormulaHelper)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    setTimeout(() => firstInputRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = prev || "unset";
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  // Quand on reçoit la méta, on reconstruit le form pour inclure tous les champs du type
  useEffect(() => {
    const cleaned = sanitizeRow(row);
    if (Array.isArray(fieldsMeta) && fieldsMeta.length) {
      const full = {};
      fieldsMeta.forEach((f) => {
        // valeur initiale = valeur existante sinon défaut (checkbox à false par défaut)
        const v = Object.prototype.hasOwnProperty.call(cleaned, f.key)
          ? cleaned[f.key]
          : f.type === "checkbox"
          ? false
          : f.default ?? "";
        full[f.key] = v;
      });
      setForm(full);
    } else {
      setForm(cleaned);
    }
  }, [row, fieldsMeta]);

  const fields = useMemo(() => {
    if (Array.isArray(fieldsMeta) && fieldsMeta.length) return fieldsMeta; // [{key,label,type,options,...}]
    // fallback: transformer les clés en objets minimalistes
    return Object.keys(form || {}).map((k) => ({ key: k, label: k }));
  }, [form, fieldsMeta]);
  if (!row) return null;

  const onChange = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!user?.uid || !typeKey || !row?.id) return;
    setSaving(true);
    try {
      const patch = diffPatch(row, form);
      if (Object.keys(patch).length) {
        await updateRelationRow(user.uid, typeKey, row.id, patch);
      }
      onSaved?.({ ...row, ...patch });
      onClose?.();
    } catch (err) {
      alert(err?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="formula-helper-overlay show" onClick={onClose}>
      <div
        className="formula-helper-modal full-page-form"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Édition relation"
      >
        <div className="formula-helper-header">
          <h3>
            <i className="fas fa-user-edit"></i> Modifier la relation
          </h3>
          <button className="formula-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="formula-helper-content full-page-content">
          <form className="full-page-form-grid" onSubmit={onSubmit}>
            {fields.map((f, idx) => (
              <div
                key={f.key}
                className={`form-group ${
                  f.type === "textarea" ? "full-width" : ""
                }`}
              >
                <label className="form-label full-page-label">
                  {safeLabel(f)}
                </label>
                {renderInput(
                  f,
                  form[f.key],
                  (v) => onChange(f.key, v),
                  idx === 0 ? firstInputRef : null
                )}
              </div>
            ))}

            <div className="modal-footer full-page-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <i className="fas fa-times"></i> Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  "Enregistrement..."
                ) : (
                  <>
                    <i className="fas fa-save"></i> Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// --- Helpers
const HIDDEN_KEYS = new Set([
  "id",
  "__id",
  "__meta",
  "__actions",
  "__deadline",
  "__sort_deadline",
  "_source",
  "synced",
]);
function sanitizeRow(row) {
  const out = {};
  Object.keys(row || {}).forEach((k) => {
    if (HIDDEN_KEYS.has(k)) return;
    const v = row[k];
    // skip éventuels timestamps Firestore
    if (v && typeof v === "object" && ("seconds" in v || "nanoseconds" in v))
      return;
    out[k] = v;
  });
  return out;
}
function diffPatch(orig, next) {
  const patch = {};
  Object.keys(next || {}).forEach((k) => {
    if (next[k] !== orig[k]) patch[k] = next[k];
  });
  return patch;
}

function renderInput(field, value, onChange, refIfFirst) {
  const t = String(field?.type || "").toLowerCase();
  const readOnly = !!field?.read_only;
  // types "forts" depuis la méta (comme RelationPage)
  if (t === "select") {
    const opts = splitOptions(field?.options);
    return (
      <select
        ref={refIfFirst}
        className="form-input"
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value=""></option>
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (t === "checkbox") {
    return (
      <label className="checkbox-label">
        <input
          ref={refIfFirst}
          type="checkbox"
          checked={!!value}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span style={{ marginLeft: 8 }}>{safeLabel(field)}</span>
      </label>
    );
  }
  if (t === "date") {
    const iso = toISODate(value);
    return (
      <input
        ref={refIfFirst}
        type="date"
        className="form-input"
        value={iso}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (t === "number") {
    return (
      <input
        ref={refIfFirst}
        type="number"
        className="form-input"
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (t === "textarea") {
    return (
      <textarea
        ref={refIfFirst}
        rows={6}
        className="form-input full-page-textarea"
        value={value ?? ""}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", resize: "vertical" }}
      />
    );
  }
  // image : simple URL/texte par défaut (à spécialiser plus tard si besoin)
  return (
    <input
      ref={refIfFirst}
      type="text"
      className="form-input"
      value={value ?? ""}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
function safeLabel(f) {
  return f?.label || f?.name || f?.key || "";
}
function splitOptions(s) {
  if (!s) return [];
  return String(s)
    .split(/[;,\|]/g)
    .map((t) => t.trim())
    .filter(Boolean);
}

function isNumberLike(v) {
  return (
    typeof v === "number" ||
    (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v.trim()))
  );
}
function isDateLike(v) {
  if (typeof v !== "string") return false;
  const d = Date.parse(v);
  return (
    (!Number.isNaN(d) && /^\d{4}-\d{2}-\d{2}/.test(v)) ||
    /\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/.test(v)
  );
}
function toISODate(v) {
  try {
    const d = new Date(v);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}
